import {
    Flex,
    Text,
    Button,
    Divider,
    Box,
    Link,
    Image,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    useToast,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
} from '@chakra-ui/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { BrowserView, isMobile } from 'react-device-detect';
import { AuthenticatedComponent } from '../auth/keycloak';
import { CurrencyForPayout } from '../component/profileComponents/CurrencyForPayout';
import { useEffect, useState } from 'react';
import {
    WrapperLicense,
    getProductByType,
    payoutStreamgold,
    ProductDef,
    StreamGoldId,
    PayoutNotAvailableError,
} from '../service/inventory';
import {
    createStripeAccount,
    getStripeAccount,
    getStripeDashboard,
    StripeAccount,
} from '../service/social';
import { CurrencyDisplay, fetchCurrencyBalances } from '../util/storeUtil';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { isStripeAccountSetup } from '../service/stripe';
import { InformationIcon } from '../component/iconComponents/InformationIcon';
import { CustomCheckboxGroup } from '../component/CustomCheckboxGroup';

export const DashboardWalletPage: AuthenticatedComponent = ({ keycloak }) => {
    const [balances, setBalances] = useState<CurrencyDisplay[]>([]);
    const [stripeAccount, setStripeAccount] = useState<StripeAccount | undefined>(undefined);
    const [stripeDashboardUrl, setStripeDashboardUrl] = useState<string>('');
    const [products, setProducts] = useState<ProductDef[]>([]);
    const [selectProductIndex, setProductSelect] = useState<number>(0);
    const [canPayout, setCanPayout] = useState<boolean>(false);
    const [availableQuantity, setAvailableQuantity] = useState<number>(0);
    const [pendingQuantity, setPendingQuantity] = useState<number>(0);
    const [refreshPayout, setRefreshPayout] = useState<boolean>(false);
    const [isPayoutOpen, setIsPayoutOpen] = useState<boolean>(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
    const toast = useToast();

    const getProduct = async () => {
        try {
            const products = await getProductByType('payout', WrapperLicense, keycloak.token);
            setProducts(products);
        } catch (err: any) {
            console.error(err);
        }
    };

    const getBalances = async () => {
        const balances = await fetchCurrencyBalances(keycloak.token, keycloak.subject);
        setBalances(balances);
    };

    const stripeSignUp = async () => {
        try {
            let returnUrl = `${window.location.origin}/dashboard`;
            if (isMobile) {
                returnUrl += '/wallet';
            }
            const accountLink = await createStripeAccount(
                keycloak.subject,
                keycloak.token,
                returnUrl,
                returnUrl,
            );
            if (accountLink !== undefined) {
                window.location.replace(accountLink?.url);
            }
        } catch (err: any) {
            console.error(err);
        }
    };

    const checkStripeAccount = async () => {
        try {
            const account = await getStripeAccount(keycloak.subject, keycloak.token);
            setStripeAccount(account);
            stripeDashboard();
        } catch (err: any) {
            console.error(err);
        }
    };

    const stripeDashboard = async () => {
        try {
            const dashboard = await getStripeDashboard(keycloak.subject, keycloak.token);
            if (dashboard !== undefined) {
                setStripeDashboardUrl(dashboard.url);
            }
        } catch (err: any) {
            console.error(err);
        }
    };

    const requestPayout = async () => {
        try {
            if (selectProductIndex >= 0 && selectProductIndex < products.length) {
                await payoutStreamgold(products[selectProductIndex].product_id, keycloak.token);
                getBalances();
                setRefreshPayout(true);
                setIsConfirmOpen(true);
            }
        } catch (err: any) {
            console.error(err);
            toast({
                title: 'Failed to make Withdrawal',
                description: (err as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
        setIsPayoutOpen(false);
    };

    const setProductValue = (index: number) => {
        setProductSelect(index);

        if (index >= 0 && index < products.length) {
            if (
                products[index].cost &&
                products[index].cost.length > 0 &&
                products[index].cost[0].cost_type === StreamGoldId &&
                products[index].cost[0].quantity <= availableQuantity
            ) {
                setCanPayout(true);
                return;
            }
        }

        setCanPayout(false);
    };

    const quantityCallback = (available: number, pending: number) => {
        setAvailableQuantity(available);
        setPendingQuantity(pending);
    };

    useEffect(() => {
        getProduct();
        getBalances();
        checkStripeAccount();
    }, []);

    const renderContent = () => (
        <Flex className={'dashboard-container'}>
            <Divider className="divider" />
            <CurrencyForPayout
                keycloak={keycloak}
                quantityCallback={quantityCallback}
                refreshFlag={refreshPayout}
            />
            <Divider className="divider" />
            <Flex className={'wallet-header'}>
                <Text className={'wallet-heading'}>In Game Currencies:</Text>
                <Popover>
                    <PopoverTrigger>
                        <IconButton
                            variant={'unstyled'}
                            icon={<InformationIcon />}
                            aria-label={'available-balance'}
                        />
                    </PopoverTrigger>
                    <PopoverContent>
                        <PopoverHeader>In Game Currencies:</PopoverHeader>
                        <PopoverBody>
                            Currencies you hold from games built on the STREAMSIX platform.
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            </Flex>
            <Flex className={'wallet-game-currency'}>
                {balances.map((currency, idx) => (
                    <Flex
                        className={idx % 2 !== 0 ? 'wallet-row' : 'wallet-row wallet-row-stripe'}
                        key={idx}
                    >
                        <Text className={'wallet-gamename'}>{currency.gameName}</Text>
                        <Image src={currency.image} />
                        <Text>
                            {currency.quantity} {currency.displayName}
                        </Text>
                    </Flex>
                ))}
            </Flex>
            {/* <Flex className={'divider'} /> */}
            <Flex className={'wallet-withdraw'}>
                {isStripeAccountSetup(stripeAccount) ? (
                    <>
                        <CustomCheckboxGroup
                            options={products.map((product) => product.product_name)}
                            onValueChange={setProductValue}
                        />
                        <Button
                            isDisabled={
                                !canPayout || keycloak.hasResourceRole('disable_payout', 'realm')
                            }
                            onClick={() => setIsPayoutOpen(true)}
                        >
                            Make Withdrawal
                        </Button>
                        {stripeDashboardUrl && (
                            <Button onClick={() => window.open(stripeDashboardUrl, '_blank')}>
                                Stripe Dashboard
                            </Button>
                        )}
                    </>
                ) : (
                    <Button onClick={stripeSignUp}>Sign Up With Stripe</Button>
                )}
                {isMobile && (
                    <Link as={ReactRouterLink} to={'/dashboard/transactions'}>
                        Transaction History
                    </Link>
                )}
            </Flex>
        </Flex>
    );

    const renderPayoutModal = (productList: ProductDef[]) => {
        return (
            <Modal
                isOpen={isPayoutOpen}
                onClose={() => {
                    setIsPayoutOpen(false);
                }}
                isCentered
            >
                <ModalOverlay />
                <ModalContent className={'wallet-modal'}>
                    <Flex className={'wallet-modal-content'}>
                        <Text>Please confirm your exchange:</Text>
                        <Text>{productList[selectProductIndex].product_name}</Text>
                        <Flex className={'divider'} />
                        <Flex className={'wallet-modal-buttons'}>
                            <Button onClick={() => setIsPayoutOpen(false)}>Cancel</Button>
                            <Button className={'confirm-button'} onClick={() => requestPayout()}>
                                Confirm
                            </Button>
                        </Flex>
                    </Flex>
                </ModalContent>
            </Modal>
        );
    };

    const renderConfirmModal = () => {
        return (
            <Modal
                isOpen={isConfirmOpen}
                onClose={() => {
                    setIsConfirmOpen(false);
                }}
                isCentered
            >
                <ModalOverlay />
                <ModalContent className={'wallet-modal'}>
                    <Flex className={'wallet-modal-content'}>
                        <Text>Payout successful!</Text>
                        <Flex className={'divider'} />
                        <Flex className={'wallet-modal-buttons'}>
                            <Button
                                onClick={() => {
                                    setRefreshPayout(true);
                                    setIsConfirmOpen(false);
                                }}
                            >
                                Close
                            </Button>
                            <Button
                                className={'confirm-button'}
                                onClick={() => window.open(stripeDashboardUrl, '_blank')}
                            >
                                View in Stripe
                            </Button>
                        </Flex>
                    </Flex>
                </ModalContent>
            </Modal>
        );
    };

    return (
        <>
            <BrowserView>
                <Box>{renderContent()}</Box>
                {products.length > 0 && renderPayoutModal(products)}
                {renderConfirmModal()}
            </BrowserView>
            <MobileViewPage
                title="MY WALLET"
                titleColor="#FFF"
                titleBackgroundColor="#6867dd"
                isLoading={!balances || !products || !stripeAccount}
            >
                {renderContent()}
                {products.length > 0 && renderPayoutModal(products)}
                {renderConfirmModal()}
            </MobileViewPage>
        </>
    );
};
